import { useCallback, useEffect, useState } from 'react'
import {
  normaliseServerUrl,
  setServer,
  setSessionEndedHandler,
  storedServer,
  storedSession,
} from './api/client'
import { me, probeServer, register, signIn, signOut } from './api/endpoints'
import type { Organisation, ServerInfo, User } from './api/types'
import { myOrganisations } from './api/endpoints'
import { ConnectScreen } from './pages/ConnectScreen'
import { RegisterScreen } from './pages/RegisterScreen'
import { SignInScreen } from './pages/SignInScreen'
import { OrganisationPicker } from './pages/OrganisationPicker'
import { Dashboard } from './pages/Dashboard'

type Phase =
  | { name: 'loading' }
  | { name: 'connect' }
  | { name: 'signIn'; serverName: string; notice?: string }
  | { name: 'register'; serverName: string }
  | { name: 'pickOrganisation'; user: User; organisations: Organisation[] }
  | { name: 'dashboard'; user: User; organisation: Organisation }

/**
 * The shell: connect to a server, sign in, choose which organisation you are running, then work.
 *
 * Each step is a phase rather than a route because they are strictly sequential — there is nothing
 * useful to show somebody who has not chosen a server, and a URL that lands them there would only
 * bounce them back.
 */
export default function App() {
  const [phase, setPhase] = useState<Phase>({ name: 'loading' })
  const [serverInfo, setServerInfo] = useState<ServerInfo | null>(null)

  const enter = useCallback(async (user: User) => {
    const result = await myOrganisations()
    const organisations = result.ok ? result.value : []

    // One organisation is not a choice, so it is not presented as one. Most clubs have exactly one.
    if (organisations.length === 1) {
      setPhase({ name: 'dashboard', user, organisation: organisations[0] })
    } else {
      setPhase({ name: 'pickOrganisation', user, organisations })
    }
  }, [])

  // A session that ended underneath us -- revoked, expired past recovery, or issued by a server
  // that has since been rebuilt. Without this the dashboard stays on screen showing the last good
  // picture while every poll is refused, which looks like a frozen game rather than a sign-out.
  useEffect(() => {
    setSessionEndedHandler(() => {
      setPhase((current) => {
        if (current.name === 'connect' || current.name === 'signIn') return current
        return {
          name: 'signIn',
          serverName: '',
          notice: 'Your session ended. Sign in again to carry on.',
        }
      })
    })
    return () => setSessionEndedHandler(null)
  }, [])

  // Restore what we can on load: a saved server, then a saved session.
  useEffect(() => {
    void (async () => {
      // Nothing saved, so try where this page came from. When the dashboard is served by the
      // instance itself -- which is how the compose file deploys it -- the answer is already known
      // and asking for it would be asking somebody to type the address they just visited.
      if (!storedServer()) {
        setServer(window.location.origin)
        const sameOrigin = await probeServer()
        if (!sameOrigin.ok || sameOrigin.value.product !== 'tacmaps') {
          // Hosted somewhere else, or opened from a file. Ask.
          setServer('')
          setPhase({ name: 'connect' })
          return
        }
      }

      const probe = await probeServer()
      if (!probe.ok) {
        setPhase({ name: 'connect' })
        return
      }
      setServerInfo(probe.value)

      if (!storedSession()) {
        setPhase({ name: 'signIn', serverName: probe.value.name })
        return
      }

      const profile = await me()
      if (!profile.ok) {
        signOut()
        setPhase({ name: 'signIn', serverName: probe.value.name })
        return
      }

      await enter(profile.value)
    })()
  }, [enter])

  switch (phase.name) {
    case 'loading':
      return <Splash />

    case 'connect':
      return (
        <ConnectScreen
          initial={storedServer()}
          onConnected={(url, info) => {
            setServer(normaliseServerUrl(url))
            setServerInfo(info)
            setPhase({ name: 'signIn', serverName: info.name })
          }}
        />
      )

    case 'signIn':
      return (
        <SignInScreen
          serverName={phase.serverName}
          notice={phase.notice}
          allowRegistration={serverInfo?.allowRegistration ?? false}
          onSignIn={async (email, password) => {
            const result = await signIn(email, password)
            if (result.ok) await enter(result.value)
            return result
          }}
          onRegister={() =>
            setPhase({ name: 'register', serverName: phase.serverName })
          }
          onChangeServer={() => setPhase({ name: 'connect' })}
        />
      )

    case 'register':
      return (
        <RegisterScreen
          serverName={phase.serverName}
          onRegister={async (details) => {
            const result = await register(details)
            if (result.ok) await enter(result.value)
            return result
          }}
          onCancel={() => setPhase({ name: 'signIn', serverName: phase.serverName })}
        />
      )

    case 'pickOrganisation':
      return (
        <OrganisationPicker
          user={phase.user}
          organisations={phase.organisations}
          onPick={(organisation) =>
            setPhase({ name: 'dashboard', user: phase.user, organisation })
          }
          onSignOut={() => {
            signOut()
            setPhase({ name: 'signIn', serverName: '' })
          }}
          onCreated={() => void enter(phase.user)}
        />
      )

    case 'dashboard':
      return (
        <Dashboard
          user={phase.user}
          organisation={phase.organisation}
          onSwitchOrganisation={() => void enter(phase.user)}
          onSignOut={() => {
            signOut()
            setPhase({ name: 'signIn', serverName: '' })
          }}
        />
      )
  }
}

function Splash() {
  return (
    <div
      style={{
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        color: 'var(--text-tertiary)',
      }}
    >
      Connecting…
    </div>
  )
}
